import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { functions } from '../../firebase-client';
import { useMyTeams } from '../teams/useMyTeams';
import { Card } from '../ui/card';
import './dashboardEventCheckInCard.css';

const loadActiveCheckIns = httpsCallable(functions, 'getMyActiveEventCheckIns');
const REFRESH_INTERVAL_MS = 60 * 1000;

function timingLabel(startsAt, now = new Date()) {
  const start = new Date(startsAt);
  const difference = start.getTime() - now.getTime();
  if (difference <= 0) return 'In progress';
  const minutes = Math.max(1, Math.ceil(difference / 60000));
  if (minutes < 60) return `Starts in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `Starts in ${hours} hr${remainder ? ` ${remainder} min` : ''}`;
}

function scannerPath(opportunity) {
  if (opportunity.sourceType === 'course') {
    const base = `/teams/${encodeURIComponent(opportunity.teamId)}/courses/${encodeURIComponent(opportunity.courseId)}/check-in`;
    return opportunity.sessionId
      ? `${base}?session=${encodeURIComponent(opportunity.sessionId)}`
      : base;
  }
  const base = `/teams/${encodeURIComponent(opportunity.teamId)}/events/${encodeURIComponent(opportunity.eventId)}/check-in`;
  return opportunity.occurrenceId
    ? `${base}?occurrence=${encodeURIComponent(opportunity.occurrenceId)}`
    : base;
}

export default function DashboardEventCheckInCard() {
  const { teams, loading: teamsLoading } = useMyTeams();
  const teamIds = useMemo(() => (teams || []).map(team => team.id).filter(Boolean), [teams]);
  const teamIdsKey = teamIds.join('|');
  const [opportunities, setOpportunities] = useState([]);
  const [now, setNow] = useState(() => new Date());

  const refresh = useCallback(async () => {
    if (!teamIds.length || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      setOpportunities([]);
      return;
    }
    try {
      const response = await loadActiveCheckIns({ teamIds });
      setOpportunities(Array.isArray(response.data?.opportunities) ? response.data.opportunities : []);
      setNow(new Date());
    } catch {
      setOpportunities([]);
    }
  }, [teamIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!teamsLoading) refresh();
  }, [teamsLoading, refresh]);

  useEffect(() => {
    const intervalId = window.setInterval(refresh, REFRESH_INTERVAL_MS);
    window.addEventListener('online', refresh);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('online', refresh);
    };
  }, [refresh]);

  if (!opportunities.length) return null;

  return (
    <Card className="dashboard-card dashboard-check-in-card" aria-labelledby="dashboard-check-in-title">
      <div className="dashboard-check-in-card__header">
        <div>
          <p className="dashboard-check-in-card__eyebrow">Ready when doors open</p>
          <h2 id="dashboard-check-in-title">Event and course check-in</h2>
        </div>
        <span className="dashboard-check-in-card__live" aria-label="Check-in available">Available</span>
      </div>
      <div className="dashboard-check-in-card__events">
        {opportunities.map(opportunity => (
          <article className="dashboard-check-in-card__event" key={`${opportunity.teamId}:${opportunity.sourceType || 'event'}:${opportunity.courseId || opportunity.eventId}:${opportunity.sessionId || opportunity.occurrenceId || 'event'}`}>
            <div className="dashboard-check-in-card__details">
              <div className="dashboard-check-in-card__club">{opportunity.teamName}</div>
              <h3>{opportunity.eventName}</h3>
              <div className="dashboard-check-in-card__time">
                <span>{new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(opportunity.startsAt))}</span>
                <span aria-hidden="true">·</span>
                <strong>{timingLabel(opportunity.startsAt, now)}</strong>
              </div>
            </div>
            <Link className="button button-primary dashboard-check-in-card__action" to={scannerPath(opportunity)}>
              Open scanner
            </Link>
          </article>
        ))}
      </div>
    </Card>
  );
}

export { scannerPath, timingLabel };
